import { DxDataGridComponent } from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { BaseEntity, BaseState } from '../entities/BaseEntities';
import { BaseComponent } from '../component/BaseComponent';
import { BasePageComponent } from '../component/BasePageComponent';
import { RC, ResourcesService } from '@core/framework/resources.service';
import { EnumGridModes, EnumPageDisplayModes } from '@core/framework/sysenums.enum';
import { Utils } from '@core/helpers/Utils';
import { AppInjector } from '@core/injector/injector.module';
import { IBaseComponent } from './../component/IBaseComponent';
import { UiService } from '@core/framework/ui.service';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { Column, Properties } from 'devextreme/ui/data_grid';
import { FrameworkService } from '@m4u-angular/core/framework/framework.service';
import { AppConfigService } from '@m4u-angular/configuration/app.config.service';
import { formatDate } from 'devextreme/localization';

const clearBody = () => {
    let el = document.body.firstElementChild;
    while (el) {
        document.body.removeChild(el);
        el = document.body.firstElementChild;
    }
};


export class GridCellEditHandlers {
    constructor
        (
            public BeforeEdit?: string,
            public AfterEdit?: string,
            public AfterEditPromise?: string,
            public AfterEditAsync?: string
        ) { };
}


export class GridCellEditParams {
    constructor
        (
            public value: any,
            public rowData: any,
            public changes: any,
            public rowIndex: number
        ) { }
}


export class GridCellEditAsyncParams {
    constructor
        (
            public value: any,
            public rowData: any,
            public rowIndex: number
        ) { }
}


export class GridCellOptionsParams {
    constructor
        (
            public value: any,
            public rowData: any,
            public rowIndex: number
        ) { }
}

export class GridCellOptionHandlers {
    public that?: any;
    constructor
        (
            public QueryEditCell?: string,
            public QueryShowCell?: string
        ) { }
}

class DefferedPromise<T> extends Promise<T>
{
    constructor() {
        let resolve, reject;
        super((_resolve, _reject) => {
            resolve = _resolve;
            reject = _reject;
        });
        this.Resolve = resolve;
        this.Reject = reject;
    }
    Resolve: (value?: T | PromiseLike<T>) => void;
    Reject: (reason?: any) => void;
}

export class GridExtender {

    //Subscribtions
    private subscribtions: Subscription[] = [];


    //Other
    private MapEventsEdit: { [ColumnName: string]: GridCellEditHandlers; } = {};
    private MapEventsOptions: { [ColumnName: string]: GridCellOptionHandlers; } = {};
    private currentRowIndex: number;
    public Mode: EnumGridModes;
    private AllowCustomization: boolean;
    private AllowAdvancedFilters: boolean;
    private BeforePrintDataGridOptions: any;
    private BeforePrintRefs: { body: any[], gridParent: HTMLElement | null, gridEl: HTMLElement | null; } = {
        body: [],
        gridEl: null,
        gridParent: null
    };

    private Component: IBaseComponent;
    private isValid: DefferedPromise<boolean> = new DefferedPromise<boolean>();
    public IsAutoResizingEnabled = true; // auto grid resizing
    public EntityTypeName: string;

    //Link main objects
    public grid: DxDataGridComponent;
    public get gridAny(): any { return this.grid; }
    public get CurrentRowIndex(): number { return this.currentRowIndex; }
    public constructor(grid: DxDataGridComponent, mode: EnumGridModes, allowCustomization: boolean, allowAdvancedFilters: boolean,
        component: IBaseComponent, entityTypeName: string) {
        this.grid = grid;
        this.Mode = mode;
        this.AllowCustomization = allowCustomization;
        this.AllowAdvancedFilters = allowAdvancedFilters;
        this.Component = component;
        this.EntityTypeName = entityTypeName;
        this.isValid.Resolve(true);
    }

    //InitCompactMode(compactModeValue: boolean) {
    //    const compactModeClass = 'grid-compact-mode';
    //    const gridClassList = this.grid.instance.element().classList;
    //    compactModeValue ? gridClassList.add(compactModeClass) : gridClassList.remove(compactModeClass);
    //    this.grid.instance.repaint();
    //}

    //Shortcut for grid editing
    public Get(ColumnName: string, RowIndex: number): any {
        if (this.IsColumnExists(ColumnName) && this.IsRowExists(RowIndex)) return this.grid.instance.cellValue(RowIndex, ColumnName);
    }

    //Shortcut for grid editing
    public Set(ColumnName: string, RowIndex: number, oValue: any) {
        if (this.IsColumnExists(ColumnName) && this.IsRowExists(RowIndex)) {
            this.grid.instance.cellValue(RowIndex, ColumnName, oValue);
        }
    }

    public AcceptChanges() {
        this.grid.instance.saveEditData();
    }

    public HasEditData() {
        return this.grid.instance.hasEditData();
    }

    public CancelEditData() {
        this.grid.instance.cancelEditData();
    }

    public SetGridFilter(ColumnName: string, ComparisonOperator: string, oValue: any) {
        this.grid.instance.filter([ColumnName, ComparisonOperator, oValue]);
    }

    public Refresh() {
        return this.grid.instance.refresh();
    }

    public GetGridHeightRelativeToPage(page: BasePageComponent<BaseEntity, BaseState>): any {
        const gridRect = this.grid.instance.element().getBoundingClientRect();
        // If grid is empty there is no need to fit the full screen height
        if (this.GetRowCount() === 0) return "auto";

        // Calculate grid height fit to the page
        let height = window.innerHeight - gridRect.top;
        if (page && (page.DisplayMode === EnumPageDisplayModes.Window || page.DisplayMode === EnumPageDisplayModes.Dialog)) {
            const bottomIndent = window.innerHeight - document.querySelector('.m4u-popup .page-body.dx-scrollview').getBoundingClientRect().bottom;
            height -= bottomIndent;
        } else {
            // Get page footer height
            const pageFooters = document.querySelectorAll('.page-container m4u-footer');
            const pageFooter = pageFooters[pageFooters.length - 1];
            const pageFooterHeight = pageFooter && pageFooter['offsetParent'] && (pageFooter['offsetParent'].offsetHeight - pageFooter['offsetTop']) || 0;
            height -= pageFooterHeight;
        }

        // Get layout padding
        const pageContainers = document.getElementsByClassName('page-body');
        if (!pageContainers || !pageContainers.length) return 'auto';

        const currentLayoutPadding = +document.defaultView
            .getComputedStyle(pageContainers[pageContainers.length - 1], '')
            .getPropertyValue('padding-bottom').match(/\d/g).join('');
        const layoutPadding = this.grid.summary.totalItems && this.grid.summary.totalItems.length ? 0 : currentLayoutPadding;
        // If less than header and one row add window height
        return (height < 160 ? height + window.innerHeight : height) - currentLayoutPadding; // Layout padding, page footer
    }

    public GetSelectedRowKeys(): number[] {
        return this.grid.instance.getSelectedRowKeys();
    }

    //Shortcut for grid editing with changes object
    //Provides safe column checking
    //Returns data considering changes that were made but not saved yet
    public GetData(ColumnName: string, e: GridCellEditParams | any): any {
        if (this.IsColumnExists(ColumnName)) {
            if (e instanceof GridCellEditParams) {
                return e.changes[ColumnName] != undefined ? e.changes[ColumnName] : e.rowData[ColumnName];
            }
            else {
                return e[ColumnName];
            }
        }
    }

    //Shortcut for grid editing with changes object
    //Provides safe column checking
    //Returns previous data for this column
    public GetPrevData(ColumnName: string, e: GridCellEditParams | GridCellEditAsyncParams): any {
        if (this.IsColumnExists(ColumnName)) {
            return e.rowData[ColumnName];
        }
    }

    //Shortcut for grid editing with rowData object
    //Provides safe column checking
    public SetData(ColumnName: string, oValue: any, e: GridCellEditParams | any) {
        if (this.IsColumnExists(ColumnName)) {
            if (e instanceof GridCellEditParams) {
                e.changes[ColumnName] = oValue;
            }
            else {
                e[ColumnName] = oValue;
            }
        }
    }



    //Shortcut for grid editing
    public GotoCell(ColumnName: string, RowIndex: number): any {
        if (this.IsColumnExists(ColumnName) && this.IsRowExists(RowIndex)) return this.grid.instance.editCell(RowIndex, ColumnName);
    }

    //Check if column exists
    public IsColumnExists(ColumnName: string): any {
        if (this.GetColumn(ColumnName) == undefined) {
            throw (`Column ${ColumnName} does not exist`);
        }
        return true;
    }

    //Return column by name
    public GetColumn(ColumnName: string): any {
        return this.grid.columns.find((x: any) => x.dataField == ColumnName);
    }

    public SetColumnVisibility(ColumnName: string, visible: boolean): void {
        this.grid.instance.columnOption(ColumnName, 'visible', visible);
    }

    //Check if row exists
    public IsRowExists(RowIndex: number): any {

        var iCount: number = this.GetRowCount();
        if (RowIndex < 0 || (RowIndex > iCount && iCount != -1)) {
            throw (`Row ${RowIndex} does not exist`);
        }
        return true;
    }

    public GetRowCount(): number {
        var iCount: number = this.grid.instance.totalCount();
        return iCount == -1 ? this.grid.instance.getVisibleRows().length : iCount;
    }

    private IsRowsExists() {
        return this.grid.instance.totalCount() > 0 || this.grid.instance.getVisibleRows().length > 0;
    }

    public async ExportExcel() {
        const framework = AppInjector.get(FrameworkService);
        const formats = [framework.CompanyInfo.NumberPrice, framework.CompanyInfo.NumberSum,
        framework.CompanyInfo.NumberRate, framework.CompanyInfo.NumberPercent];

        setTimeout(async () => {
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet('Main sheet');

            await exportDataGrid({
                component: this.grid.instance,
                worksheet: worksheet,
                ...this.grid.export,
                customizeCell: (options) => {
                    // @ts-ignore
                    if (this.grid.export?.customizeExcelCell) {
                        // @ts-ignore
                        this.grid.export.customizeExcelCell(options);
                    }

                    const { gridCell, excelCell } = options;
                    const { value, column } = gridCell;

                    if (gridCell.rowType === 'group') {
                        if (typeof value === 'string') {
                            const [, caption] = value.split(';');
                            excelCell.value = caption ?? value;
                        }
                    }

                    if (gridCell.rowType === 'data' && column.dataType === 'date' && column.format) {
                        excelCell.value = formatDate(value, column.format);
                    }

                    if (gridCell.rowType === 'data' || gridCell.rowType === 'totalFooter') {
                        if (typeof column.format === 'string') {
                            const columnFormat = column.format;
                            excelCell.numFmt = columnFormat;

                            if (gridCell.rowType === 'data' && formats.indexOf(columnFormat) >= 0) {
                                excelCell.alignment = 'right';
                            }
                        }
                    }
                }
            });

            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${this.grid.export?.texts?.exportTo}.xlsx`);
        }, 0);
    }

    //Handler to remove current row
    RemoveCurrentRow(e) {
        if (!this.IsRowDeletable(e)) return;
        this.grid.instance.deleteRow(e.rowIndex);
    }

    //Handler for new row creation
    CreateNewRow() {
        if (!this.IsNewRowCreatable()) return;
        if (!this.grid.instance.hasEditData()) {
            this.grid.instance.addRow();
        }
        else {
            this.grid.instance.saveEditData()
                .then(() => this.grid.instance.addRow())
                .catch(() => {
                    AppInjector.get(UiService).InfoMessage(RC('msgFinishEditing'));
                });

        }
        this.grid.instance.focus(this.grid.instance.getCellElement(0, 0));
    }



    //Bind context this to the event. It cannot be done other way
    private BindHandler(that: any, ColumnName: string, sHandlerName: string) {
        if (sHandlerName == undefined) return;
        if (that[sHandlerName] == undefined) throw (`Invalid handler was specified for ${ColumnName}`);
        that[sHandlerName] = that[sHandlerName].bind(that);
    }

    /*
     * Attach handler event:
     * We have 4 events to choose from:
     * 1 - Before event - can be used for custom validation and such (not sure if needed yet). Can return false to prevent default operation
     * 2 - After event - can be used to update e.changes (through G.EditSet) directly on the row. Can return false to prevent default operation
     * 3 - AfterPromise event - can be used to return a promise that devextreme grid built-in mechanism will resolve
     *     the promise can still set values using e.changes (throug G.EditSet) directly on the row
     * 4 - AfterAsync event - this one is fired after grid has finished processing, and value has been saved to the underlying row
     *     this can be used for operations that relly on the underlying row value for their processing
     *     This handler can not longer access the e.changes, but it has access to the rowIndex and can modify rows directly 
     *     as any other operation using G.Set
    */
    public AttachColumnEditHandler(that: BaseComponent<BaseState>, ColumnName: string, Handlers: GridCellEditHandlers) {
        //Make sure column and handler both exists
        if (!this.IsColumnExists(ColumnName)) return;
        this.MapEventsEdit[ColumnName] = Handlers;

        //Bind handlers
        this.BindHandler(that, ColumnName, Handlers.BeforeEdit);
        this.BindHandler(that, ColumnName, Handlers.AfterEdit);
        this.BindHandler(that, ColumnName, Handlers.AfterEditPromise);
        this.BindHandler(that, ColumnName, Handlers.AfterEditAsync);


        //Bind the event to the column
        var column = this.GetColumn(ColumnName);
        column.setCellValue = this.ColumnEditHandler.bind(this, that, ColumnName, undefined);

    }

    /*
     * Attach event handler:
     * We have 2 events to choose from:
     * 1 - QueryEditCell - handler should return false if cell should not be editable
     * 2 - QueryPaintCell - handler should return false if cell should not be visible
     */
    public AttachColumnOptionsHandler(that: BaseComponent<BaseState>, ColumnName: string, Handlers: GridCellOptionHandlers) {

        //Make sure column and handler both exists
        if (!this.IsColumnExists(ColumnName)) return;
        this.MapEventsOptions[ColumnName] = Handlers;

        //Bind handlers
        Handlers.that = that;
        this.BindHandler(that, ColumnName, Handlers.QueryEditCell);
        this.BindHandler(that, ColumnName, Handlers.QueryShowCell);

        //Bind form.customizeItem function to allow hiding items in form
        //Perform the binding only once
        if (this.grid.editing.form == undefined) this.grid.editing.form = {};
        if (this.grid.editing.form.customizeItem == undefined) this.grid.editing.form.customizeItem = this.ColumnCustomizeItemHandler.bind(this, that);
    }


    //Hide items in form edit mode
    private ColumnCustomizeItemHandler(that, item) {
        //See that we have a handler
        var handlers: GridCellOptionHandlers = this.MapEventsOptions[item.dataField];
        if (handlers == undefined || handlers.QueryShowCell == undefined) return;

        //Execute handler
        var row = this.grid.instance.getVisibleRows()[this.currentRowIndex].data;
        var args = new GridCellOptionsParams(this.GetData(item.dataField, row), row, this.currentRowIndex);
        var bPaint = handlers.that[handlers.QueryShowCell](args);

        //Set value
        item.visible = bPaint;

    }



    //Raise dependant events when newData is set by the handler
    //This is done for consistancy - so that the same event handling mechanism can handle also value changes as a result of other event handlers, 
    //just like it happens when using cellValue programatically from Async handler, or just outside code
    //This procedure determines the changes made based on the previous version of newData (originalDate) and calls the matching handlers
    //for each column that such change took place
    private RaiseDependantEvents(that, ColumnName, originalData, newData, currentRowData) {

        //Get list of values that were updated during this handler - compare new vs original        
        var columnsUpdated: string[] = [];
        for (var key in newData) {
            if (key != ColumnName && (originalData == undefined || newData[key] != originalData[key])) {
                columnsUpdated.push(key);
            }
        }

        //Preserve state at this event
        var originalData = Utils.CloneObject(newData);

        //Process only those that were updated
        for (var column of columnsUpdated) {
            //See if we have handlers for the updated column
            var handlers: GridCellEditHandlers = this.MapEventsEdit[column];
            if (handlers == undefined) continue;

            //Raise event
            this.ColumnEditHandler(that, column, originalData, newData, newData[column], currentRowData);
        };
    }

    private ColumnEditHandler(that, ColumnName, originalData, newData, value, currentRowData) {
        //Ignore initial settings
        //When user resets a test field, data is set to ""
        if (value == undefined) return;

        console.info(`Editing cell: ${ColumnName} new value: ${value}`);
        if (ColumnName == 'ItemDescription') debugger;


        //Prepare
        var handlers: GridCellEditHandlers = this.MapEventsEdit[ColumnName];
        var args: GridCellEditParams = new GridCellEditParams(value, currentRowData, newData, undefined);

        //Execute the before event. If it returns false, exit
        if (handlers == undefined) debugger;
        if (handlers.BeforeEdit != undefined) {
            var handlerBefore = that[handlers.BeforeEdit];
            if (!handlerBefore(args)) return;
        }

        //Set value just on the column that was really edited 
        //(basically execute the default behaviour)
        if (originalData == undefined) this.grid.instance.columnOption(ColumnName).defaultSetCellValue(newData, value);


        //Execute after event.
        if (handlers.AfterEdit != undefined) {
            var handlerAfter = that[handlers.AfterEdit];
            handlerAfter(args);
            this.RaiseDependantEvents(that, ColumnName, originalData, newData, currentRowData);
        }


        //Execute the after event asynchronically
        //This is to be used for cases when we need the editable value to be already set in the grid
        //In such case, any modifications that need to be done, should be done only using G.Set and not through changes, since
        //changes was already updated at this point.
        if (handlers.AfterEditAsync != undefined) {

            var argsAsync = new GridCellEditAsyncParams(args.value, args.rowData, this.currentRowIndex);
            if (argsAsync.rowIndex != -1) {
                var handlerAsync = that[handlers.AfterEditAsync];
                setTimeout(() => handlerAsync(argsAsync), 100);
            }

        }

        //Execute the after event. 
        //Handler must call resolve() inside
        //It is here after the async event on purpose - 
        //since if user has specified both the regular and async event, the regular event must return a promise
        //At any case, the order of the regular and async events is random and should not be relied upon
        if (handlers.AfterEditPromise != undefined) {
            var handlerPromise = that[handlers.AfterEditPromise];
            return new Promise((resolve) => {
                const finalize = () => {
                    this.RaiseDependantEvents(that, ColumnName, originalData, newData, currentRowData);
                    if (originalData == undefined) console.info(`Saving cell: ${ColumnName}`, newData);
                    resolve(true);
                };
                handlerPromise(args, finalize.bind(this));
            });
        }

        if (originalData == undefined) console.info(`Saving cell: ${ColumnName}`, newData);


    }


    //Returns whether new row can be created now
    public IsNewRowCreatable(): boolean {
        var method: any = this.grid.editing.allowAdding;
        var bResult;
        if (method instanceof Function) {
            bResult = method();
        }
        else {
            bResult = method;
        }
        return bResult;
    }

    //Returns whether row can be edited now
    public IsRowEditable(row: any): boolean {
        return this.IsRowEditableOrUpdatable(row, this.grid.editing.allowUpdating);
    }

    //Returns whether row can be deleted now
    public IsRowDeletable(row: any): boolean {
        return this.IsRowEditableOrUpdatable(row, this.grid.editing.allowDeleting);
    }

    //Check if the operation is allowed for the row
    //Executes the bound function
    private IsRowEditableOrUpdatable(e: any, method: any): boolean {
        var bResult: boolean;

        var args =
        {
            component: this.grid.instance,
            row:
            {
                rowType: e.rowType,
                data: e.data
            }
        };
        if (method instanceof Function) {
            bResult = method(args);
        }
        else {
            bResult = method;
        }
        return bResult;
    }

    //Enable editing on click
    private OnRowClick(e) {
        if (!this.IsRowEditable(e)) return;
        if (this.grid.instance.hasEditData()) this.grid.instance.cancelEditData();
        this.grid.instance.editRow(e.rowIndex);
    }


    //Preserve current row
    //getRowIndexByKey will return -1 if it's a new row. For cellValue purposes, we can use 0 for that
    private OnEditingStart(e) {
        this.currentRowIndex = e.component.getRowIndexByKey(e.key);
        if (this.currentRowIndex == -1) this.currentRowIndex = 0;
    }

    //When adding new row, set the row index
    private OnInitNewRow(e) {
        this.currentRowIndex = 0;
    }

    //Add readonly class to rows that cannot be edited
    private OnRowPrepared(e) {
        if (e.rowType != 'data' && e.rowType != 'detail') return;
        if (!this.IsRowEditable(e)) e.rowElement.classList.add('dx-state-readonly');
    }


    //Various adjustments to editor
    private OnEditorPreparing(e) {
        if (e.parentType != 'dataRow') return;


        //See if we have handlers for the updated column
        var handlers: GridCellOptionHandlers = this.MapEventsOptions[e.dataField];
        if (handlers == undefined) return;

        //Execute handlers
        //If painting not allowed, editing is disabled as well
        var args = new GridCellOptionsParams(this.GetData(e.dataField, e.row.data), e.row.data, this.currentRowIndex);
        var bAllowEdit = (handlers.QueryEditCell != undefined ? handlers.that[handlers.QueryEditCell](args) : true);
        var bPaint = (handlers.QueryShowCell != undefined ? handlers.that[handlers.QueryShowCell](args) : true);
        if (!bPaint) bAllowEdit = false;
        if (bAllowEdit) return;

        //If both painting and editing is now allowed, and we're in cell mode, don't even create the editor
        //Otherwise, set it to readonly
        //This way, cells that are not pained will not show editor even when focusing on them
        if (this.grid.editing.mode == 'cell' && !bPaint) {
            e.cancel = true;
        }
        else {
            e.editorOptions.readOnly = true;
            e.editorOptions.tabIndex = -1;
        }

    }

    //Make cell as readonly and hide
    private OnCellPrepared(e) {
        //Handle rows only
        if (e.rowType != 'data') return;

        //See if we have handlers for the updated column
        var handlers: GridCellOptionHandlers = this.MapEventsOptions[e.column.dataField];
        if (handlers == undefined) return;

        //Execute handler, and mark as readonly
        if (handlers.QueryEditCell != undefined) {
            var args = new GridCellOptionsParams(this.GetData(e.column.dataField, e.row.data), e.row.data, e.rowIndex);
            var bAllowEdit = handlers.that[handlers.QueryEditCell](args);
            if (!bAllowEdit) e.cellElement.classList.add('dx-state-readonly');
        }

        //Execute handler, and mark as invisible
        if (handlers.QueryShowCell != undefined) {
            var args = new GridCellOptionsParams(this.GetData(e.column.dataField, e.row.data), e.row.data, e.rowIndex);
            var bAllowEdit = handlers.that[handlers.QueryShowCell](args);
            if (!bAllowEdit) e.cellElement.classList.add('m4u-hide-text');
        }

    }


    //See if need to skip column
    private OnFocusCellChanging(e) {

        //See if moving forward or backward
        var bForward: boolean = (e.newColumnIndex > e.prevColumnIndex || e.newRowIndex > e.prevRowIndex);

        while (true) {

            //See if this is a special column. Skip them
            var bSpecial = e.columns[e.newColumnIndex].type != undefined ? true : false;
            if (!bSpecial) {
                //Get column focus is moving to
                var sColumnName: string = e.columns[e.newColumnIndex].dataField;

                //See if we have handlers for this column
                var handlers: GridCellOptionHandlers = this.MapEventsOptions[sColumnName];
                if (handlers == undefined) return;

                //Execute handler. If editing is allowed, we can break execution
                var row = e.rows[e.newRowIndex].data;
                var args = new GridCellOptionsParams(this.GetData(sColumnName, row), row, e.newRowIndex);
                var bAllowEdit: boolean = handlers.that[handlers.QueryEditCell](args);
                if (bAllowEdit) return;
            }


            //When moving forward
            if (bForward) {

                //When not last column - move to next
                if (e.newColumnIndex < e.columns.length - 1) {
                    e.newColumnIndex += 1;
                }
                //When last column - 
                else {
                    e.newColumnIndex = 0;
                    e.newRowIndex += 1;
                }
            }
            //When moving backward
            else {
                //When not first column - move to prev
                if (e.newColumnIndex > 0) {
                    e.newColumnIndex -= 1;
                }
                else
                //When first column
                {
                    e.newColumnIndex = e.columns.length - 1;
                    e.newRowIndex -= 1;
                }

            }

        }

    }
    //!!!THIS IS WORKING BECAUSE OF PROMISE VALUE IS IMMUTABLE AFTER RESOLVE/REJECT
    // validation sequence is:
    // create new deferred for IsValid
    // trigger grid's saveEditData
    // if grid in EDIT mode 
    //      then OnRowValidating will be triggered where IsValid will be resolved
    //      else saveEditData().then will be called and IsValid will be resolved;

    public ValidateGrid(): Promise<boolean> {
        this.isValid = new DefferedPromise<boolean>();
        this.grid.instance.saveEditData().then(() => this.isValid.Resolve(true));
        return this.isValid;
    }

    private OnRowValidating(e) {
        if (e.status === "pending") {
            e.promise.then(result => this.isValid.Resolve(result.isValid));
        }
        else {
            this.isValid.Resolve(e.isValid);
        }
    }

    //Sync column visibility and order in form with the one set by the user
    private OnOptionChanged(e) {
        if (e.name == 'columns' && (e.fullName.endsWith('visible') || e.fullName.endsWith('visibleIndex'))) {
            this.grid.instance.beginUpdate();
            for (var i = 0; i < this.grid.instance.columnCount(); i++) {
                var colAny = this.grid.instance.columnOption(i);

                this.grid.instance.columnOption(i,
                    {
                        formItem:
                        {
                            visible: colAny.visible,
                            visibleIndex: colAny.visibleIndex
                        }
                    });
            }
            this.grid.instance.endUpdate();
        }
    }



    //Initialization entry point
    public InitGrid() {
        this.InitGridGeneral();
        switch (this.Mode) {
            case EnumGridModes.Editable:
                this.InitGridEditable();
                break;
            case EnumGridModes.Report:
                this.InitGridReport();
                break;
            case EnumGridModes.Lines:
                this.InitGridLines();
                break;
        }

    }

    //Generic stuff for all grids
    private InitGridGeneral() {

        //Set storage key for storing the user settings
        if (!this.grid.stateStoring) this.grid.stateStoring = {};
        this.grid.stateStoring.storageKey = `${this.Component.ComponentStateName}.Grid.State`;

        // Set file name for excel export
        if (!this.grid.export || this.grid.export?.texts?.exportTo === 'DataGrid') {
            let sFileName: string = this.Component.ComponentStateName.replace('Component', '').replace('Grid', '');
            if (sFileName.endsWith('s')) { sFileName += 'es'; } else { sFileName += 's'; }
            this.grid.export = { texts: { exportTo: sFileName }};// { fileName: sFileName };
        }

        //Add class to grid if it's clickable
        const grid = this.grid;
        this.subscribtions.push(this.grid.onInitialized.subscribe((e) => {
            const gridRowClickEvent = grid.onRowClick;
            if (gridRowClickEvent && gridRowClickEvent.observed || grid.focusedRowEnabled) {
                e.element.classList.add('grid-row-clickable');
            }

            //HACK use custom props for saving custom grid config
            (this.grid.instance as any).AllowCustomization = this.AllowCustomization;
            (this.grid.instance as any).AllowAdvancedFilters = this.AllowAdvancedFilters;

            const resourceService = AppInjector.get(ResourcesService);
            const appConfigService = AppInjector.get(AppConfigService);

            const gridColumns = this.grid.instance.option('columns') || [];
            gridColumns.forEach((column: Column) => {
                let entityTypeName;
                if (!Utils.IsEmpty(this.EntityTypeName)) {
                    entityTypeName = this.EntityTypeName;
                } else {
                    const entityType = (this.Component as any)?.EntityType;
                    if (!entityType) return;

                    const entityInstance = new entityType();
                    entityTypeName = entityInstance && (entityInstance as BaseEntity).TypeName;
                }
                if (Utils.IsEmpty(entityTypeName) || Utils.IsEmpty(column.dataField)) return;

                Utils.IsEmpty(column.caption) && (column.caption = resourceService.RC(`BL.${entityTypeName}.${column.dataField}`,
                    appConfigService.AppConfig.ModuleName));
            });

            //disable hiding or dilters if doesn't allow
            const gridOptions = {
                allowColumnReordering: this.AllowCustomization,
                allowColumnResizing: this.AllowCustomization,
                /*stateStoring: {
                    enabled: this.AllowCustomization
                },*/
                columns: gridColumns
            };
            this.grid.instance.option(gridOptions);

        }));
    }

    public Print(): void {
        const watcher = this.BeforePrint();

        this.PrepareForExport("print");

        window.onafterprint = () => {
            watcher.unsubscribe();

            this.AfterPrint();
        };
    }

    private CreatePrintHeader() {
        const pageHeader = document.createElement('div');
        pageHeader.className = 'page-print-header';

        // @ts-ignore
        const Page = this.Component.Page;
        if (!Page) return null;

        const title = Page.GetTitle();
        const printTitle = Page.OnGetPrintTitle();
        const filterDescription = Page.OnGetFilterDescription();

        const printTitleHtml = `<div class="print-title"> ${printTitle}</div>`;
        const filterDescriptionHtml = `<div style="white-space: pre-line">${filterDescription}</div>`;
        pageHeader.innerHTML = `
        <div>
            <div class="title"> ${title}</div>
            ${printTitle ? printTitleHtml : ''}
            ${filterDescription ? filterDescriptionHtml : ''}
        </div>
        <div class="print-logo">
        </div>`;

        return pageHeader;
    }

    private CreateLoader() {
        const loader = document.createElement('div');
        loader.className = "loader-container";
        loader.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            background: white;
            z-index: 99999;
        `;

        loader.innerHTML = `
        <div class="loader">
            <div class="loader-inner ball-spin-fade-loader">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
            </div>
        </div>
        <div id="loader-message">Rendering...</div>`;

        return loader;
    }

    private BeforePrint() {
        const gridEl = this.grid.instance.$element()[0];
        const gridInst = this.grid.instance;

        // Save references for body children and grid parent
        this.BeforePrintRefs = {
            // @ts-ignore
            body: [...document.body.childNodes],
            gridEl,
            gridParent: gridEl.parentElement
        };

        clearBody();

        // Create page wrapper to define page width
        const page = document.createElement('div');
        page.setAttribute('size', 'A4');
        document.body.appendChild(page);

        const printHeader = this.CreatePrintHeader();
        printHeader && page.appendChild(printHeader);

        page.appendChild(this.CreateLoader());

        // Append exiting grid to page
        page.appendChild(gridEl);

        // Call print with delay and and cancel callback
        const executePrint = () => {
            const id = setTimeout(() => window.print(), 1000);

            return () => clearTimeout(id);
        };

        // Generator expands all rows
        // Executes print
        function* createPrintGenerator() {
            yield gridInst.expandAll();

            let cancelPrevPrint = null;
            while (true) {
                if (cancelPrevPrint) cancelPrevPrint();
                cancelPrevPrint = executePrint();
                yield;
            }
        }

        const printGen = createPrintGenerator();
        return this.grid.onContentReady.subscribe(() => {
            printGen.next();
        });
    }

    private AfterPrint() {
        clearBody();

        const { body, gridParent, gridEl } = this.BeforePrintRefs;

        // Restore all body children
        body.forEach(el => {
            document.body.appendChild(el);
        });

        // Restore grid element
        gridParent.appendChild(gridEl);

        this.grid.instance.option(this.BeforePrintDataGridOptions);
        this.grid.instance.repaint();
    }

    private PrepareForExport(mode: string) {
        const collectColumns = (level: number, bandColumns?: Column[]): Column[] => {
            const columns = [];
            const visibleCols = this.grid.instance.getVisibleColumns(level);
            let bandIndex = bandColumns ? bandColumns.findIndex(col => col.isBand) : undefined;

            if (visibleCols.length) {
                let isBand = false;
                let prevOwnedBand = undefined;

                const fullColumnsWidth = Math.round(visibleCols
                    .filter(x => x.type !== 'buttons' && x.type !== 'selection' && x.type !== 'adaptive' && !x.cssClass?.includes('hide-on-print'))
                    .map(x => x['visibleWidth'])
                    .reduce((sum, current) => sum += current, 0));

                visibleCols.forEach(col => {
                    const column = { ...col };
                    if (col.isBand) isBand = true;

                    if (col.ownerBand !== undefined) {
                        if (prevOwnedBand !== undefined && col.ownerBand !== prevOwnedBand) {
                            bandIndex++;
                        }

                        prevOwnedBand = col.ownerBand;
                        column.ownerBand = bandIndex;
                    }

                    if (col.type === 'buttons' || col.type === 'selection' || col.type === 'adaptive' || col.cssClass?.includes('hide-on-print')) {
                        return;
                    }
                    const currentWidth = col['visibleWidth'];
                    const columnWidth = Math.round((currentWidth / fullColumnsWidth) * 100);
                    column.width = `${columnWidth}%`;

                    columns.push(column);
                });

                if (isBand) {
                    columns.push(...collectColumns(level + 1, visibleCols));
                }
            }

            return columns;
        };

        const columns = collectColumns(0);

        const printOptions: Properties = {
            allowColumnResizing: false,
            allowColumnReordering: false,
            wordWrapEnabled: true,
            columnHidingEnabled: false,
            focusedRowIndex: -1,
            selection: {
                showCheckBoxesMode: "none"
            },
            scrolling: {
                mode: 'standard'
            },
            searchPanel: {
                visible: false
            },
            height: 'auto',
            columns: columns
        };

        if (!this.BeforePrintDataGridOptions) {
            this.BeforePrintDataGridOptions = Utils.CloneObject(this.grid.instance.option());
            //clear redundant options
            this.BeforePrintDataGridOptions = Object.keys(this.BeforePrintDataGridOptions)
                .filter(key => key in printOptions)
                .reduce((options, key) => (options[key] = this.BeforePrintDataGridOptions[key], options), {});
        }

        this.grid.instance.option(printOptions);
        this.grid.instance.refresh();
    }

    //Initialize report grid
    private InitGridReport() {
        //Show adaptive row for reports
        this.grid.columnHidingEnabled = false;
    }

    //Initialize editable grid
    private InitGridEditable() {
        //Add delete column that looks normal
        this.grid.columns =
            [{
                type: "buttons",
                width: 32,
                allowReordering: false,
                allowResizing: false,
                buttons: [{ name: "delete" }]
            }];

        if (!this.grid.sorting) this.grid.sorting = {};
        this.grid.sorting.mode = "none";

        if (!this.grid.paging) this.grid.paging = {};
        this.grid.paging.pageSize = 99999;

        //Subscribe to events
        this.subscribtions.push(this.grid.onRowClick.subscribe((e) => this.OnRowClick(e)));
        this.subscribtions.push(this.grid.onEditingStart.subscribe((e) => this.OnEditingStart(e)));
        this.subscribtions.push(this.grid.onInitNewRow.subscribe((e) => this.OnInitNewRow(e)));
        this.subscribtions.push(this.grid.onEditorPreparing.subscribe((e) => this.OnEditorPreparing(e)));
        this.subscribtions.push(this.grid.onFocusedCellChanging.subscribe((e) => this.OnFocusCellChanging(e)));
        this.subscribtions.push(this.grid.onCellPrepared.subscribe((e) => this.OnCellPrepared(e)));
        this.subscribtions.push(this.grid.onOptionChanged.subscribe((e) => this.OnOptionChanged(e)));
        this.subscribtions.push(this.grid.onRowPrepared.subscribe((e) => this.OnRowPrepared(e)));
        this.subscribtions.push(this.grid.onRowValidating.subscribe((e) => this.OnRowValidating(e)));
    }

    private InitGridLines() {
        this.InitGridReport();

        this.grid && this.grid.instance && (this.grid.instance as any).option(
            {
                'selection.mode': 'single',
                'selection.deferred': true,
                'hoverStateEnabled': true,
            });
    }



    //Unsubscribe
    public DestroyGrid() {
        this.subscribtions.forEach(o => o.unsubscribe());
    }

    OnDoubleSizePopup(e, data) {
        if (this.grid.editing.mode == 'form') return;
        var iWidth: number = data.column.visibleWidth * 2;
        e.component._popup.option('width', iWidth);
    }

    public OnGridClick() {
        if (!this.IsRowsExists()) this.CreateNewRow();
    }
}
